<template>
  <loader v-bind="{ loading }" :text="`Loading ${Naming.Asset} Overview`">
    <div class="box p-2">
      <div class="columns p-1 has-border-thin-grey rounded-sml">
        <div class="column is-5 dashboard-box rounded-sml">
          <div class="is-flex align-start">
            <asset-image :image-url="asset.image_url"/>
            <div>
              <h1 class="is-3 title is-marginless has-text-weight-medium">
                <span class="has-text-weight-normal">{{ asset.code || asset.id }}</span>
              </h1>
              <div v-if="assetCustomFields" class="">
                <p v-for="(field, index) in assetCustomFields" :key="index" class="has-text-grey">
                  {{ field.label }}:
                  <span class="has-text-weight-medium">{{ field.value ? field.value : '--' }}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-3 has-text-centered is-flex-column align-center dashboard-box">
          <div class="has-text-centered is-flex is-flex-column align-items-center">
            <qr-code class="" style="width: 90px; margin-bottom: 0.25rem" :text="asset.barcode.data"/>
            <small>{{ asset.barcode.data }}</small>
          </div>
          <div class="overview-heading mt-1">
            <div>
              {{ Naming.Asset }} Risk
            </div>
            <current-risk class="is-size-6 is-marginless" :risk="asset.risk"/>
            <p>{{ asset.risk.level }}</p>
          </div>
        </div>
        <div class="column is-4 overview-list">
          <div>
            Created: <span>{{ asset.created_at | date('L LTS') }}</span>
          </div>
          <div>
            Last {{ Naming.Inspection }}:
            <span v-if="overview.latestInspection" class="has-tooltip"
                  :data-tooltip="`${overview.latestInspection.created_at}`">
                {{ overview.latestInspection.created_at | date('L LTS') }}</span>
            <span v-else>Unavailable</span>
          </div>
          <div>
            Next {{ Naming.Inspection }}:
            <span v-if="asset.next_checklist_inspection">
                &nbsp;{{ asset.next_checklist_inspection.next_inspection | date('L') }}</span>
            <span v-else>Unavailable</span>
          </div>
          <div>
            {{ Naming.Asset }} Type: <span>{{ asset.type.name }}</span>
          </div>
          <div>
            {{ Naming.Site }}: <span>{{ asset.zone.site.name }}</span>
          </div>
          <div>
            {{ Naming.Zone }}: <span>{{ asset.zone.name }}</span>
          </div>
          <div>
            {{ Naming.Asset }} Status:
            <current-status
                class="is-size-7"
                v-if="asset.current_status.value"
                :status="asset.current_status"/>
            <p v-else>Unavailable</p>
          </div>
        </div>
      </div>
      <div class="is-flex justify-between columns mt-2" v-if="hasUpcomingInspections">
        <div class="column is-6 p-0 pr-1" v-if="shouldShowOnlyUpcomingInspections">
          <table class="table has-border-thin-grey">
            <caption class="is-uppercase title is-6">Upcoming {{ Naming.Inspections }}</caption>
            <thead>
            <tr>
              <th>{{ Naming.Checklist }}</th>
              <th class="is-narrow">Next {{ Naming.Inspection }} Date</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="upcomingInspection in onlyUpcomingInspections" :key="upcomingInspection.id">
              <td>
                <router-link
                    v-if="$root.hasAbility('manage-checklists', 'Billow\\Models\\Job') && upcomingInspection.checklist_id"
                    class="has-text-info"
                    :to="{ name: 'checklist-edit', params: { checklist: upcomingInspection.checklist_id }}">
                  <span>{{ upcomingInspection.checklist_name }}</span>
                </router-link>
              </td>
              <td class="is-narrow has-text-right">
                {{ upcomingInspection.next_inspection | date( 'L' ) }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="column is-6 p-0 pl-1" v-if="shouldShowOnlyOverdueInspections">
          <table class="table has-border-thin-grey">
            <caption class="is-uppercase title is-6">Overdue {{ Naming.Inspections }}</caption>
            <thead>
            <tr>
              <th>{{ Naming.Checklist }}</th>
              <th class="is-narrow">Next {{ Naming.Inspection }} Date</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="overdueInspection in overdueInspections" :key="overdueInspection.id">
              <td class="has-text-info">
                <router-link
                    v-if="$root.hasAbility('manage-checklists', 'Billow\\Models\\Job') && overdueInspection.checklist_id"
                    class="has-text-info"
                    :to="{ name: 'checklist-edit', params: { checklist: overdueInspection.checklist_id }}">
                  <span>{{ overdueInspection.checklist_name }}</span>
                </router-link>
              </td>
              <td class="is-narrow has-text-right">
                {{ overdueInspection.next_inspection | date( 'L' ) }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="columns mt-2" v-if="requiredDocuments.length">
        <div class="column">
          <h4 class="is-uppercase has-text-centered title is-6 mb-1">Required Documents</h4>
          <table class="table has-border-thin-grey is-fullwidth">
            <thead>
            <tr>
              <th>Required Document</th>
              <th>Uploaded</th>
              <th>Expiry Date</th>
              <th>Status</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="requirement in requiredDocuments" :key="requirement.uuid">
              <td>{{ requirement.description }}</td>
              <td>
                <span v-if="requirement.document">{{ requirement.document.updated_at | date('L') }}</span>
              </td>
              <td>
                <span v-if="requirement.document && requirement.document.expires_at">
                  {{ requirement.document.expires_at | date('L') }}
                </span>
                <span v-else-if="requirement.document">No expiry date</span>
              </td>
              <td>
                <span class="tag" :class="requirement.satisfied ? 'is-success' : 'is-warning'">
                  {{ requirement.satisfied ? 'Uploaded' : 'Missing' }}
                </span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="columns mt-2">
        <div class="column">
          <h4 class="is-uppercase has-text-centered title is-6 mb-1">Last Recorded Location</h4>
          <div v-if="asset.location && asset.location.latitude">
            <GmapMap
                :center="{ lat: asset.location.latitude, lng: asset.location.longitude }"
                :zoom="16"
                :options="googleMapOptions"
                map-type-id="terrain"
                style="width: 100%; height: 300px">
              <GmapMarker :position="{ lat: asset.location.latitude, lng: asset.location.longitude }"/>
            </GmapMap>
            <p class="has-text-centered has-text-weight-bold mt">
              {{ 'lat: ' + asset.location.latitude + ' - lng: ' + asset.location.longitude }}
            </p>
          </div>
          <div v-else class="has-background-cool-blue is-flex align-center justify-center"
               style="height: 100px; width: 100%;">
            <div class="has-text-centered">
              <icon class="is-large" icon="map-marker-alt 3x"/>
              <p>Location not enabled.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </loader>
</template>
<script>
import {mapGetters} from 'vuex'
import defaultMapStyles from '@/theme/gmaps/default'
import moment from "moment";
import AssetImage from "@/views/asset/partials/AssetImage.vue";

export default {
  components: {AssetImage},

  data: () => ({
    loading: true,
    googleMapOptions: {
      styles: defaultMapStyles
    }
  }),

  created() {
    Promise.all([
      this.$store.dispatch('asset/loadOverview', this.$route.params.asset),
      this.$store.dispatch('inspection/getAssetUpcomingInspections', this.$route.params.asset),
      this.$store.dispatch('documents/loadRequiredDocuments', this.$route.params.asset),
    ]).then(() => {
      this.loading = false
    })
  },

  computed: {
    ...mapGetters('asset', [
      'asset',
      'overview'
    ]),
    ...mapGetters('inspection', ['upcomingInspections']),
    ...mapGetters('documents', ['requiredDocuments']),
    assetCustomFields() {
      let customFields = this.asset.type.custom_fields_object
      let customFieldValues = this.asset.meta_data || []

      return _.map(customFields, field => {
        field.value = customFieldValues[field.field_id]

        return field
      })
    },
    overdueInspections() {
      return _.filter(this.upcomingInspections, upcomingInspection => moment(upcomingInspection.next_inspection).isBefore(moment()))
    },
    onlyUpcomingInspections() {
      return _.filter(this.upcomingInspections, upcomingInspection => moment(upcomingInspection.next_inspection).isSameOrAfter(moment()))
    },
    hasUpcomingInspections() {
      return this.upcomingInspections?.length > 0
    },
    shouldShowOnlyUpcomingInspections() {
      return this.onlyUpcomingInspections.length > 0
    },
    shouldShowOnlyOverdueInspections() {
      return this.overdueInspections.length > 0
    }
  },
}
</script>